/* ============================================================
   Love, Londy — "Design a Charm" product page.
   Depends on product-data.js (THEMES, CORDS, BEADS, EXAMPLE_PHOTOS,
   groupBy, and the pure render helpers) being loaded first.

   This file owns: the swatch pickers, add-on toggles, price/preview
   display, validation, and the REAL Shopify cart wiring (this is the
   part that replaces the old prototype's in-memory demo cart).
   ============================================================ */

/* ===================== CONFIG (from the Liquid-rendered script tag) ===================== */
const CHARM_CONFIG = JSON.parse(document.getElementById("charm-config-data").textContent);

const BASE_PRICE = 27;
const DUO_BASE_PRICE = 35;
const CORD_ADDON_PRICE = 5;
const NAME_ADDON_PRICE = 12;

/* ===================== STATE ===================== */
let qty = 1;
let charmType = "signature"; // 'signature' | 'duo'
const addonState = { cord2: false, cord3: false, name2: false, name3: false, dcord2: false, dcord3: false };

/* ===================== CHARM TYPE ===================== */
function selectCharmType(type) {
  charmType = type;
  document.getElementById("ctype-signature").classList.toggle("selected", type === "signature");
  document.getElementById("ctype-duo").classList.toggle("selected", type === "duo");
  document.getElementById("group-signature").style.display = type === "signature" ? "block" : "none";
  document.getElementById("group-duo").style.display = type === "duo" ? "block" : "none";

  const eyebrow = document.getElementById("product-eyebrow");
  const title = document.getElementById("product-title");
  document.getElementById("product-desc").style.display = "none";
  const baseLabel = document.getElementById("price-base-label");
  if (type === "duo") {
    eyebrow.textContent = "Love, Londy Duo";
    baseLabel.textContent = "base $35 · 2 names, 1 cord";
  } else {
    eyebrow.textContent = "Love, Londy Signature";
    baseLabel.textContent = "base $27 · 1 name, 1 cord";
  }

  document.querySelectorAll(".field.invalid").forEach(f => f.classList.remove("invalid"));
  onChange();
}

/* ===================== SWATCH PICKERS (cord / theme / bead) ===================== */
let pickerKind = null;      // 'cord' | 'theme' | 'bead'
let pickerTarget = null;    // target hidden-input id (cord/bead) …
let pickerThemeLinks = null; // …or {cordId, beadId} when kind === 'theme'

function cordThumbStyle(idx) {
  const c = CORDS[idx];
  if (!c) return "";
  if (c.photo) return `background-image:url('${c.photo}');`;
  return cordStyle(idx);
}

/* ---------- SOLD-OUT CORDS ----------
   Cord stock is counted by hand, so the shop owner lists the cords they've
   run out of in the "Sold-out cords" theme setting (one per line, or comma
   separated). Those cords still show in the picker — greyed out and not
   selectable — so customers can't order a cord that can't be made. */
const SOLD_OUT_CORDS = String(CHARM_CONFIG.soldOutCords || "")
  .split(/[\n,]/)
  .map(s => s.trim().toLowerCase())
  .filter(Boolean);

function cordIsSoldOut(cordIdx) {
  const cord = CORDS[cordIdx];
  return !!(cord && SOLD_OUT_CORDS.includes(cord.name.trim().toLowerCase()));
}

function beadThumbHTML(idx) {
  const b = BEADS[idx];
  if (!b) return "";
  const s = beadStyle(idx);
  const shapeClass = b.type === "dice" ? "bead-thumb-dice" : "bead-thumb-round";
  return `<div class="bead-thumb ${shapeClass}" style="${s.style}"></div>`;
}

function openCordPicker(targetId) {
  pickerKind = "cord";
  pickerTarget = targetId;
  const groups = groupBy(CORDS, c => c.family);
  const currentVal = document.getElementById(targetId).value;
  document.getElementById("picker-title").textContent = "Choose Your Cord";
  document.getElementById("picker-subtitle").textContent = "";
  document.getElementById("picker-body").innerHTML = groups.map(group => `
    <div class="swatch-family">${group.name}</div>
    <div class="swatch-grid">
      ${group.items.map(i => {
        const out = cordIsSoldOut(i);
        return `
        <button type="button" class="swatch-card-btn ${String(i) === currentVal ? 'selected' : ''}${out ? ' is-sold-out' : ''}"
                ${out ? 'disabled aria-disabled="true"' : `onclick="selectPickerItem(${i})"`}>
          <div class="swatch-card-img" style="${cordThumbStyle(i)}">${CORDS[i].badge && !out ? `<span class="theme-badge">${CORDS[i].badge}</span>` : ""}${out ? `<span class="sold-out-flag">Sold out</span>` : cordIsHoundstooth(i) ? `<span class="hs-flag">${HOUNDSTOOTH_MAX_LETTERS} char max</span>` : ""}</div>
          <div class="swatch-card-name">${CORDS[i].name}</div>
          ${!out && cordIsHoundstooth(i) ? `<span class="cord-tip">Best for names <b>${HOUNDSTOOTH_MAX_LETTERS} characters or less</b> &mdash; longer names are made on the closest matching color cord.</span>` : ""}
        </button>
      `;}).join("")}
    </div>
  `).join("");
  document.getElementById("picker-overlay").classList.add("show");
}

function openThemePicker(themeId, cordId, beadId) {
  pickerKind = "theme";
  pickerTarget = themeId;
  pickerThemeLinks = { cordId, beadId };
  const groups = groupBy(THEMES, t => t.category);
  const currentVal = document.getElementById(themeId).value;
  document.getElementById("picker-title").textContent = "Pick Your Colors";
  document.getElementById("picker-subtitle").textContent = "Theme colors create a vibe.";
  document.getElementById("picker-body").innerHTML = groups.map(group => `
    <div class="swatch-family">${group.name}</div>
    <div class="swatch-grid">
      ${group.items.map(i => `
        <button type="button" class="swatch-card-btn ${String(i) === currentVal ? 'selected' : ''}" onclick="selectPickerItem(${i})">
          <div class="swatch-card-img" style="${themeSwatchStyle(i)}">${THEMES[i].badge ? `<span class="theme-badge">${THEMES[i].badge}</span>` : ""}</div>
          <div class="swatch-card-name">${THEMES[i].name}</div>
        </button>
      `).join("")}
    </div>
  `).join("");
  document.getElementById("picker-overlay").classList.add("show");
}

function openBeadPicker(targetId) {
  pickerKind = "bead";
  pickerTarget = targetId;
  const groups = groupBy(BEADS, b => b.family);
  const currentVal = document.getElementById(targetId).value;
  document.getElementById("picker-title").textContent = "Choose Your Accent Bead";
  document.getElementById("picker-subtitle").textContent = "";
  document.getElementById("picker-body").innerHTML = groups.map(group => `
    <div class="swatch-family">${group.name}</div>
    <div class="swatch-grid bead-grid">
      ${group.items.map(i => `
        <button type="button" class="swatch-card-btn bead-card ${String(i) === currentVal ? 'selected' : ''}" onclick="selectPickerItem(${i})">
          <div class="swatch-card-img bead-card-img">${beadThumbHTML(i)}</div>
          <div class="swatch-card-name">${BEADS[i].name}</div>
        </button>
      `).join("")}
    </div>
  `).join("");
  document.getElementById("picker-overlay").classList.add("show");
}

function closePicker() {
  document.getElementById("picker-overlay").classList.remove("show");
  pickerKind = null;
  pickerTarget = null;
  pickerThemeLinks = null;
}

// shared by the theme picker and the "Use This Theme" gallery button —
// sets the theme trigger plus auto-fills its linked cord + bead
function applyThemeToBlock(themeIdx, themeId, cordId, beadId) {
  const theme = THEMES[themeIdx];
  if (!theme) return;
  document.getElementById(themeId).value = themeIdx;
  updateThemeTrigger(themeId);
  const cordIdx = CORDS.findIndex(c => c.name === theme.cord);
  const beadIdx = BEADS.findIndex(b => b.name === theme.bead);
  // Don't auto-fill a cord we've run out of — leave it blank so the customer
  // picks an available one rather than ordering something we can't make.
  if (cordId && cordIdx > -1 && !cordIsSoldOut(cordIdx)) { document.getElementById(cordId).value = cordIdx; updateCordTrigger(cordId); }
  if (beadId && beadIdx > -1) { document.getElementById(beadId).value = beadIdx; updateBeadTrigger(beadId); }
}

function selectPickerItem(idx) {
  if (!pickerKind || !pickerTarget) return;
  document.getElementById(pickerTarget).value = idx;
  if (pickerKind === "cord") {
    updateCordTrigger(pickerTarget);
  } else if (pickerKind === "bead") {
    updateBeadTrigger(pickerTarget);
  } else if (pickerKind === "theme") {
    const { cordId, beadId } = pickerThemeLinks;
    applyThemeToBlock(idx, pickerTarget, cordId, beadId);
  }
  closePicker();
  onChange();
}

function updateCordTrigger(targetId) {
  const trigger = document.getElementById("trig-" + targetId);
  if (!trigger) return;
  const val = document.getElementById(targetId).value;
  const preview = trigger.querySelector(".swatch-trigger-preview");
  const label = trigger.querySelector(".swatch-trigger-label");
  if (val !== "" && CORDS[val]) {
    preview.style.cssText = cordThumbStyle(val);
    label.textContent = CORDS[val].name;
    trigger.classList.add("has-value");
  } else {
    preview.style.cssText = "";
    label.textContent = "Choose a cord colour…";
    trigger.classList.remove("has-value");
  }
}

function updateThemeTrigger(targetId) {
  const trigger = document.getElementById("trig-" + targetId);
  if (!trigger) return;
  const val = document.getElementById(targetId).value;
  const preview = trigger.querySelector(".swatch-trigger-preview");
  const label = trigger.querySelector(".swatch-trigger-label");
  if (val !== "" && THEMES[val]) {
    preview.innerHTML = `<span class="mini-strip" style="${themeSwatchStyle(val)}"></span>`;
    label.textContent = THEMES[val].name;
    trigger.classList.add("has-value");
  } else {
    preview.innerHTML = "";
    label.textContent = "Choose a theme…";
    trigger.classList.remove("has-value");
  }
}

function updateBeadTrigger(targetId) {
  const trigger = document.getElementById("trig-" + targetId);
  if (!trigger) return;
  const val = document.getElementById(targetId).value;
  const preview = trigger.querySelector(".swatch-trigger-preview");
  const label = trigger.querySelector(".swatch-trigger-label");
  if (val !== "" && BEADS[val]) {
    preview.innerHTML = beadThumbHTML(val);
    label.textContent = BEADS[val].name;
    trigger.classList.add("has-value");
  } else {
    preview.innerHTML = "";
    label.textContent = "Choose an accent bead…";
    trigger.classList.remove("has-value");
  }
}

/* ===================== PHOTO EXAMPLE GALLERY ===================== */
let galleryMode = "live";     // 'live' | 'photo'
let activeExampleIdx = null;

const LIVE_PREVIEW_HINT = 'Live preview — an <b>illustrated approximation</b> of your gold clip, cords, beads &amp; letter charms. Every charm ships with the signature Love, Londy Charm. Your handmade piece will have natural, one-of-a-kind variation.';

function renderGalleryThumbs() {
  const el = document.getElementById("gallery-thumbs");
  if (!el) return;
  // Quick-switch thumbnails use each theme's own real product photo (THEMES),
  // not the placeholder EXAMPLE_PHOTOS set (that one's reserved for the
  // community gallery on the home page).
  el.innerHTML = `
    <button type="button" class="gallery-thumb ${galleryMode === "live" ? "active" : ""}" onclick="showGalleryLive()">
      <span class="gallery-thumb-live">✦</span>
      <span class="gallery-thumb-label">Live Preview</span>
    </button>
    ${THEMES.map((t, i) => `
      <button type="button" class="gallery-thumb ${activeExampleIdx === i ? "active" : ""}" onclick="showGalleryPhoto(${i})">
        <img class="gallery-thumb-img" src="${t.image}" alt="${t.name} finished charm">
        <span class="gallery-thumb-label">${t.name}</span>
      </button>
    `).join("")}
  `;
}

function showGalleryLive() {
  galleryMode = "live";
  activeExampleIdx = null;
  document.getElementById("live-view").classList.remove("hide");
  document.getElementById("photo-view").classList.remove("show");
  document.getElementById("photo-view-actions").style.display = "none";
  document.getElementById("preview-hint").innerHTML = LIVE_PREVIEW_HINT;
  renderGalleryThumbs();
}

function showGalleryPhoto(idx) {
  const t = THEMES[idx];
  if (!t) return;
  galleryMode = "photo";
  activeExampleIdx = idx;
  document.getElementById("live-view").classList.add("hide");
  document.getElementById("photo-view").classList.add("show");
  const img = document.getElementById("photo-view-img");
  img.src = t.image;
  img.alt = `Finished Love, Londy charm in the ${t.name} theme`;
  document.getElementById("photo-view-actions").style.display = "flex";
  document.getElementById("preview-hint").innerHTML = `<b>${t.name}</b> — a real finished piece so you can see the theme in person. Yours is made to order with the name, cord &amp; bead you choose above.`;
  renderGalleryThumbs();
}

function applyExampleTheme() {
  if (activeExampleIdx === null) return;
  const themeIdx = activeExampleIdx;
  if (!THEMES[themeIdx]) return;
  if (charmType !== "signature") selectCharmType("signature");
  applyThemeToBlock(themeIdx, "theme1", "cord1", "bead1");
  showGalleryLive();
  onChange();
}

/* ===================== ADD-ON TOGGLES ===================== */
function toggleAddon(key) {
  // enforce sequential unlock
  if (key === "cord3" && !addonState.cord2) return;
  if (key === "name3" && !addonState.name2) return;
  if (key === "dcord3" && !addonState.dcord2) return;

  addonState[key] = !addonState[key];
  document.getElementById("sw-" + key).classList.toggle("on", addonState[key]);
  document.getElementById("body-" + key).classList.toggle("show", addonState[key]);

  // cascading: turning off a 2nd-level addon turns off its 3rd-level child
  if (key === "cord2" && !addonState.cord2 && addonState.cord3) {
    addonState.cord3 = false;
    document.getElementById("sw-cord3").classList.remove("on");
    document.getElementById("body-cord3").classList.remove("show");
  }
  if (key === "name2" && !addonState.name2 && addonState.name3) {
    addonState.name3 = false;
    document.getElementById("sw-name3").classList.remove("on");
    document.getElementById("body-name3").classList.remove("show");
  }
  if (key === "dcord2" && !addonState.dcord2 && addonState.dcord3) {
    addonState.dcord3 = false;
    document.getElementById("sw-dcord3").classList.remove("on");
    document.getElementById("body-dcord3").classList.remove("show");
  }

  document.getElementById("toggle-cord3").classList.toggle("disabled", !addonState.cord2);
  document.getElementById("toggle-name3").classList.toggle("disabled", !addonState.name2);
  document.getElementById("toggle-dcord3").classList.toggle("disabled", !addonState.dcord2);
  document.getElementById("cord-helper").style.display = addonState.cord2 ? "none" : "block";
  document.getElementById("name-helper").style.display = addonState.name2 ? "none" : "block";
  document.getElementById("dcord-helper").style.display = addonState.dcord2 ? "none" : "block";

  onChange();
}

/* ===================== QTY ===================== */
function changeQty(delta) {
  qty = Math.max(1, qty + delta);
  document.getElementById("qty").innerText = qty;
  onChange();
}

/* ===================== PRICE + PREVIEW (display only — the real charge
   always comes from the Shopify variant/add-on prices at checkout) ===================== */
function computePrice() {
  if (charmType === "duo") {
    let price = DUO_BASE_PRICE;
    const lines = [{ label: "Duo Charm (2 names, 1 cord)", amount: DUO_BASE_PRICE }];
    if (addonState.dcord2) { price += CORD_ADDON_PRICE; lines.push({ label: "Additional cord", amount: CORD_ADDON_PRICE }); }
    if (addonState.dcord3) { price += CORD_ADDON_PRICE; lines.push({ label: "3rd cord", amount: CORD_ADDON_PRICE }); }
    return { unit: price, lines };
  }

  let price = BASE_PRICE;
  const lines = [{ label: "Base charm (1 name, 1 cord)", amount: BASE_PRICE }];
  if (addonState.cord2) { price += CORD_ADDON_PRICE; lines.push({ label: "Additional cord", amount: CORD_ADDON_PRICE }); }
  if (addonState.cord3) { price += CORD_ADDON_PRICE; lines.push({ label: "3rd cord", amount: CORD_ADDON_PRICE }); }
  if (addonState.name2) { price += NAME_ADDON_PRICE; lines.push({ label: "2nd name", amount: NAME_ADDON_PRICE }); }
  if (addonState.name3) { price += NAME_ADDON_PRICE; lines.push({ label: "3rd name", amount: NAME_ADDON_PRICE }); }
  return { unit: price, lines };
}

function updateSummary() {
  const { unit, lines } = computePrice();
  const total = unit * qty;
  document.getElementById("total-price").innerText = unit.toFixed(2);
  const _bp = document.getElementById("btn-price"); if (_bp) _bp.innerText = total.toFixed(2);

  let html = lines.map(l => `<div class="summary-row"><span>${l.label}</span><span>$${l.amount.toFixed(2)}</span></div>`).join("");
  if (qty > 1) html += `<div class="summary-row"><span>Quantity</span><span>×${qty}</span></div>`;
  html += `<div class="summary-row total"><span>Total</span><span>$${total.toFixed(2)}</span></div>`;
  document.getElementById("summary-box").innerHTML = html;
}

function updatePreview() {
  const strandsEl = document.getElementById("strands");
  let html = "";

  if (charmType === "duo") {
    html += renderDuoStrandHTML({
      name1: document.getElementById("name-d1").value,
      theme1Idx: document.getElementById("theme-d1").value,
      bead1Idx: document.getElementById("bead-d1").value,
      name2: document.getElementById("name-d2").value,
      theme2Idx: document.getElementById("theme-d2").value,
      bead2Idx: document.getElementById("bead-d2").value,
      cordIdx: document.getElementById("cord-d1").value,
    });
    if (addonState.dcord2) {
      html += renderStrandHTML({ cordIdx: document.getElementById("dcord2color").value, showBead: false, tailHeight: 170 });
    }
    if (addonState.dcord3) {
      html += renderStrandHTML({ cordIdx: document.getElementById("dcord3color").value, showBead: false, tailHeight: 170 });
    }
    strandsEl.innerHTML = html;
    return;
  }

  const mainCordIdx = document.getElementById("cord1").value;

  html += renderStrandHTML({
    nameVal: document.getElementById("name1").value,
    themeIdx: document.getElementById("theme1").value,
    cordIdx: mainCordIdx,
    beadIdx: document.getElementById("bead1").value,
    showBead: true,
    tailHeight: 70,
  });

  if (addonState.cord2) {
    html += renderStrandHTML({ cordIdx: document.getElementById("cord2color").value, showBead: false, tailHeight: 170 });
  }
  if (addonState.cord3) {
    html += renderStrandHTML({ cordIdx: document.getElementById("cord3color").value, showBead: false, tailHeight: 170 });
  }
  if (addonState.name2) {
    html += renderStrandHTML({
      nameVal: document.getElementById("name2").value,
      themeIdx: document.getElementById("theme2").value,
      cordIdx: document.getElementById("cordn2").value,
      beadIdx: document.getElementById("beadn2").value,
      showBead: true,
      tailHeight: 70,
    });
  }
  if (addonState.name3) {
    html += renderStrandHTML({
      nameVal: document.getElementById("name3").value,
      themeIdx: document.getElementById("theme3").value,
      cordIdx: document.getElementById("cordn3").value,
      beadIdx: document.getElementById("beadn3").value,
      showBead: true,
      tailHeight: 70,
    });
  }

  strandsEl.innerHTML = html;
}

function onChange() {
  updateSummary();
  updatePreview();
  refreshCordNotes();
}

/* ===================== VALIDATION ===================== */
function markField(id, ok) {
  const el = document.getElementById("f-" + id);
  if (!el) return;
  el.classList.toggle("invalid", !ok);
}

// Houndstooth-pattern cords are woven with a fixed repeat that only lines up
// cleanly behind a short name. This is NOT a hard block — the customer can
// still order it; we just tell them up front (and flag it on the order) that
// a longer name gets made on the closest matching color cord instead.
const HOUNDSTOOTH_MAX_LETTERS = 6;

function cordIsHoundstooth(cordIdx) {
  const cord = CORDS[cordIdx];
  return !!(cord && /houndstooth/i.test(cord.name));
}

// Which name field(s) actually sit on each cord. Add-on cords hang on the same
// charm as the main name; the duo charm carries both of its names on one cord.
const CORD_NAME_FIELDS = {
  "cord1": ["name1"],
  "cord2color": ["name1"],
  "cord3color": ["name1"],
  "cordn2": ["name2"],
  "cordn3": ["name3"],
  "cord-d1": ["name-d1", "name-d2"],
  "dcord2color": ["name-d1", "name-d2"],
  "dcord3color": ["name-d1", "name-d2"],
};

// The name(s) on this cord that blow past the houndstooth limit.
function houndstoothOverflowNames(cordFieldId) {
  const cordEl = document.getElementById(cordFieldId);
  if (!cordEl || cordEl.value === "" || !cordIsHoundstooth(Number(cordEl.value))) return [];
  return (CORD_NAME_FIELDS[cordFieldId] || [])
    .map(id => (document.getElementById(id)?.value || "").trim())
    .filter(n => n.length > HOUNDSTOOTH_MAX_LETTERS);
}

// True when this cord will actually be substituted on the finished charm.
function cordWillBeSubstituted(cordFieldId) {
  return houndstoothOverflowNames(cordFieldId).length > 0;
}

// Catches a cord that sold out after it was already chosen (e.g. the shop
// updated the sold-out list while this page sat open, or a theme filled it in
// before the list changed). Blocks checkout rather than taking the order.
function checkCordAvailable(cordFieldId) {
  const cordEl = document.getElementById(cordFieldId);
  if (!cordEl || cordEl.value === "") return true;
  const ok = !cordIsSoldOut(Number(cordEl.value));
  markField(cordFieldId, ok);
  if (!ok) {
    showToast(`${CORDS[cordEl.value].name} is sold out right now — please pick another cord.`);
  }
  return ok;
}

// Draws (or clears) the note under a cord field. Soft yellow when the cord is
// simply houndstooth, escalated orange once a name on it is too long.
function renderCordNote(cordFieldId) {
  const trigger = document.getElementById("trig-" + cordFieldId);
  if (!trigger) return;
  let note = trigger.parentNode.querySelector(".cord-note");
  const cordEl = document.getElementById(cordFieldId);
  const isHs = cordEl && cordEl.value !== "" && cordIsHoundstooth(Number(cordEl.value));

  if (!isHs) {
    if (note) note.remove();
    return;
  }
  if (!note) {
    note = document.createElement("div");
    note.className = "cord-note";
    trigger.parentNode.insertBefore(note, trigger.nextSibling);
  }

  const tooLong = houndstoothOverflowNames(cordFieldId);
  if (tooLong.length) {
    const quoted = tooLong.map(n => `“${n}”`).join(" & ");
    const verb = tooLong.length > 1 ? "are" : "is";
    note.className = "cord-note cord-note-alert";
    note.innerHTML = `<span class="cord-note-icon">!</span><div><b>Heads up —</b> this cord is intended for names ${HOUNDSTOOTH_MAX_LETTERS} characters or less. Since ${quoted} ${verb} longer, it will be made on the closest matching color cord.</div>`;
  } else {
    note.className = "cord-note";
    note.innerHTML = `<span class="cord-note-icon">i</span><div>This cord is intended for names <b>${HOUNDSTOOTH_MAX_LETTERS} characters or less</b>. For longer names it will be made on the closest matching color cord.</div>`;
  }
}

function refreshCordNotes() {
  Object.keys(CORD_NAME_FIELDS).forEach(renderCordNote);
}

function validate() {
  let ok = true;

  const req = (fieldId, elId) => {
    const val = document.getElementById(elId).value.trim();
    const good = val.length > 0;
    markField(fieldId, good);
    if (!good) ok = false;
    return good;
  };

  if (charmType === "duo") {
    req("name-d1", "name-d1");
    req("theme-d1", "theme-d1");
    req("bead-d1", "bead-d1");
    req("name-d2", "name-d2");
    req("theme-d2", "theme-d2");
    req("bead-d2", "bead-d2");
    req("cord-d1", "cord-d1");
    if (addonState.dcord2) req("dcord2color", "dcord2color");
    if (addonState.dcord3) req("dcord3color", "dcord3color");
    if (!checkCordAvailable("cord-d1")) ok = false;
    if (addonState.dcord2 && !checkCordAvailable("dcord2color")) ok = false;
    if (addonState.dcord3 && !checkCordAvailable("dcord3color")) ok = false;
    return ok;
  }

  req("name1", "name1");
  req("theme1", "theme1");
  req("cord1", "cord1");
  req("bead1", "bead1");

  if (addonState.cord2) req("cord2color", "cord2color");
  if (addonState.cord3) req("cord3color", "cord3color");

  if (addonState.name2) {
    req("name2", "name2");
    req("theme2", "theme2");
    req("cordn2", "cordn2");
    req("beadn2", "beadn2");
  }
  if (addonState.name3) {
    req("name3", "name3");
    req("theme3", "theme3");
    req("cordn3", "cordn3");
    req("beadn3", "beadn3");
  }

  if (!checkCordAvailable("cord1")) ok = false;
  if (addonState.cord2 && !checkCordAvailable("cord2color")) ok = false;
  if (addonState.cord3 && !checkCordAvailable("cord3color")) ok = false;
  if (addonState.name2 && !checkCordAvailable("cordn2")) ok = false;
  if (addonState.name3 && !checkCordAvailable("cordn3")) ok = false;

  return ok;
}

/* ===================== REAL SHOPIFY CART ===================== */
// Every add-on (extra cord, extra name) is a SEPARATE simple product/variant
// added as its own cart line right alongside the main charm line, all tagged
// with a shared hidden "_group_id" property so the cart page can visually
// group them under the charm they belong to. This is the standard no-app way
// to attach priced add-ons to a product in Shopify. See the setup guide for
// the exact products this theme expects to find in your store.
function makeGroupId() {
  return "grp_" + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
}

async function addToCart() {
  if (!validate()) {
    showToast("Please fill in all required fields ✦");
    document.querySelector(".field.invalid")?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  if (charmType === "signature" && !CHARM_CONFIG.signatureVariantId) {
    showToast("This product isn't set up yet — see the theme setup guide.");
    return;
  }
  if (charmType === "duo" && !CHARM_CONFIG.duoVariantId) {
    showToast("This product isn't set up yet — see the theme setup guide.");
    return;
  }

  const groupId = makeGroupId();
  const items = [];

  // Stamp the order line when a houndstooth cord has to be swapped for the
  // closest matching color, so it's visible on the packing slip at make time.
  const flagSubstitution = (props, cordFieldId) => {
    if (cordWillBeSubstituted(cordFieldId)) {
      props["Cord Note"] = `Name over ${HOUNDSTOOTH_MAX_LETTERS} characters — make on closest matching color cord`;
    }
    return props;
  };

  if (charmType === "duo") {
    const name1 = document.getElementById("name-d1").value.trim();
    const name2 = document.getElementById("name-d2").value.trim();
    const props = {
      "Charm Type": "Duo",
      "Name 1": name1,
      "Name 1 — Theme": THEMES[document.getElementById("theme-d1").value].name,
      "Name 1 — Accent Bead": BEADS[document.getElementById("bead-d1").value].name,
      "Name 2": name2,
      "Name 2 — Theme": THEMES[document.getElementById("theme-d2").value].name,
      "Name 2 — Accent Bead": BEADS[document.getElementById("bead-d2").value].name,
      "Cord Color": CORDS[document.getElementById("cord-d1").value].name,
      "_group_id": groupId,
    };
    const notes = document.getElementById("notes").value.trim();
    if (notes) props["Notes"] = notes;
    flagSubstitution(props, "cord-d1");

    items.push({ id: CHARM_CONFIG.duoVariantId, quantity: qty, properties: props });

    if (addonState.dcord2 && CHARM_CONFIG.cordAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.cordAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({ "For": `${name1} & ${name2}'s charm`, "Cord Color": CORDS[document.getElementById("dcord2color").value].name, "_group_id": groupId }, "dcord2color"),
      });
    }
    if (addonState.dcord3 && CHARM_CONFIG.cordAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.cordAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({ "For": `${name1} & ${name2}'s charm`, "Cord Color": CORDS[document.getElementById("dcord3color").value].name, "_group_id": groupId }, "dcord3color"),
      });
    }
  } else {
    const name1 = document.getElementById("name1").value.trim();
    const props = {
      "Charm Type": "Signature",
      "Name": name1,
      "Theme": THEMES[document.getElementById("theme1").value].name,
      "Cord Color": CORDS[document.getElementById("cord1").value].name,
      "Accent Bead": BEADS[document.getElementById("bead1").value].name,
      "_group_id": groupId,
    };
    const notes = document.getElementById("notes").value.trim();
    if (notes) props["Notes"] = notes;
    flagSubstitution(props, "cord1");

    items.push({ id: CHARM_CONFIG.signatureVariantId, quantity: qty, properties: props });

    if (addonState.cord2 && CHARM_CONFIG.cordAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.cordAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({ "For": `${name1}'s charm`, "Cord Color": CORDS[document.getElementById("cord2color").value].name, "_group_id": groupId }, "cord2color"),
      });
    }
    if (addonState.cord3 && CHARM_CONFIG.cordAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.cordAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({ "For": `${name1}'s charm`, "Cord Color": CORDS[document.getElementById("cord3color").value].name, "_group_id": groupId }, "cord3color"),
      });
    }
    if (addonState.name2 && CHARM_CONFIG.nameAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.nameAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({
          "For": `${name1}'s charm`,
          "Name": document.getElementById("name2").value.trim(),
          "Theme": THEMES[document.getElementById("theme2").value].name,
          "Cord Color": CORDS[document.getElementById("cordn2").value].name,
          "Accent Bead": BEADS[document.getElementById("beadn2").value].name,
          "_group_id": groupId,
        }, "cordn2"),
      });
    }
    if (addonState.name3 && CHARM_CONFIG.nameAddonVariantId) {
      items.push({
        id: CHARM_CONFIG.nameAddonVariantId,
        quantity: qty,
        properties: flagSubstitution({
          "For": `${name1}'s charm`,
          "Name": document.getElementById("name3").value.trim(),
          "Theme": THEMES[document.getElementById("theme3").value].name,
          "Cord Color": CORDS[document.getElementById("cordn3").value].name,
          "Accent Bead": BEADS[document.getElementById("beadn3").value].name,
          "_group_id": groupId,
        }, "cordn3"),
      });
    }
  }

  const btn = document.getElementById("add-to-cart-btn");
  const originalHtml = btn.innerHTML;
  btn.disabled = true;
  btn.style.opacity = "0.6";
  btn.innerHTML = "Adding…";

  try {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.description || "Something went wrong adding this to your cart.");
    }
    await refreshCartCount();
    const label = charmType === "duo"
      ? `${document.getElementById("name-d1").value.trim()} & ${document.getElementById("name-d2").value.trim()}`
      : document.getElementById("name1").value.trim();
    showToast(`Added ${label}'s charm to your cart ✦`);
    resetForm();
  } catch (err) {
    showToast(err.message || "Something went wrong — please try again.");
  } finally {
    btn.disabled = false;
    btn.style.opacity = "";
    btn.innerHTML = originalHtml;
  }
}

async function refreshCartCount() {
  try {
    const res = await fetch("/cart.js");
    const cart = await res.json();
    const el = document.getElementById("cart-count");
    if (el) el.innerText = cart.item_count;
  } catch (e) { /* non-fatal */ }
}

function resetForm() {
  document.getElementById("pf").reset();
  ["cord1", "cord2color", "cord3color", "cordn2", "cordn3", "cord-d1", "dcord2color", "dcord3color"].forEach(updateCordTrigger);
  ["theme1", "theme2", "theme3", "theme-d1", "theme-d2"].forEach(updateThemeTrigger);
  ["bead1", "beadn2", "beadn3", "bead-d1", "bead-d2"].forEach(updateBeadTrigger);
  ["cord2", "cord3", "name2", "name3", "dcord2", "dcord3"].forEach(k => {
    if (addonState[k]) {
      addonState[k] = false;
      document.getElementById("sw-" + k).classList.remove("on");
      document.getElementById("body-" + k).classList.remove("show");
    }
  });
  document.getElementById("toggle-cord3").classList.add("disabled");
  document.getElementById("toggle-name3").classList.add("disabled");
  document.getElementById("toggle-dcord3").classList.add("disabled");
  document.getElementById("cord-helper").style.display = "block";
  document.getElementById("name-helper").style.display = "block";
  document.getElementById("dcord-helper").style.display = "block";
  qty = 1;
  document.getElementById("qty").innerText = 1;
  document.querySelectorAll(".field.invalid").forEach(f => f.classList.remove("invalid"));
  showGalleryLive();
  onChange();
}

/* ===================== BOOT ===================== */
function boot() {
  renderGalleryThumbs();
  document.getElementById("toggle-cord3").classList.add("disabled");
  document.getElementById("toggle-name3").classList.add("disabled");
  document.getElementById("toggle-dcord3").classList.add("disabled");

  // "Use This Theme" links from the home page / Pick a Theme page arrive as
  // ?theme=<index> — pre-select that theme on the (always Signature) main fields.
  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get("theme");
  if (themeParam !== null && THEMES[themeParam]) {
    selectCharmType("signature");
    applyThemeToBlock(Number(themeParam), "theme1", "cord1", "bead1");
  }

  onChange();
}
document.addEventListener("DOMContentLoaded", boot);
